import { useState, useCallback, useEffect, useRef } from 'react';
import { FeedService, FeedEvent, DefaultService, ReactionsResponse, NewPlayPayload, LeagueOutput, PlayOutput } from '../../generated-api';
import { buildPlayDetails, computeRatingImpacts, FeedPlayDetails } from './feedUtils';
import { getRatingHistories } from '../../services/ratingHistories';

interface UseFeedResult {
  events: FeedEvent[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  reactionsMap: Record<string, ReactionsResponse>;
  playDetailsMap: Record<string, FeedPlayDetails>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useFeed(initialLimit: number = 20): UseFeedResult {
  const [events, setEvents] = useState<FeedEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [reactionsMap, setReactionsMap] = useState<Record<string, ReactionsResponse>>({});
  const [playDetailsMap, setPlayDetailsMap] = useState<Record<string, FeedPlayDetails>>({});
  const leaguesRef = useRef<LeagueOutput[] | null>(null);
  
  // Use ref to access current events without adding it to dependency array
  const eventsRef = useRef<FeedEvent[]>([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const fetchReactionsBatch = useCallback(async (playIds: string[]) => {
    if (playIds.length === 0) return {};
    
    try {
      // Use batch endpoint if available, otherwise fall back to individual calls
      const playIdsStr = playIds.join(',');
      const batchResponse = await DefaultService.getReactionsBatchPlaysReactionsBatchGet({
        playIds: playIdsStr,
      });
      return batchResponse as Record<string, ReactionsResponse>;
    } catch (err) {
      console.error('Failed to fetch reactions batch, falling back to individual calls:', err);
      // Fallback to individual calls if batch endpoint fails
      const reactions: Record<string, ReactionsResponse> = {};
      await Promise.all(
        playIds.map(async (playId) => {
          try {
            const response = await DefaultService.getPlayReactionsPlaysPlayIdReactionsGet({
              playId,
            });
            reactions[playId] = response;
          } catch (e) {
            // Silently fail for individual reactions
            console.error(`Failed to fetch reactions for play ${playId}:`, e);
          }
        })
      );
      return reactions;
    }
  }, []);

  // Placements, date, duration and leagues come from the plays
  // themselves: one call for the newest plays covers almost every feed page,
  // older ones are fetched one by one. Missing details just keep the short card.
  const fetchPlayDetails = useCallback(async (playEvents: NewPlayPayload[]) => {
    if (playEvents.length === 0) return {};
    try {
      const [recent, leagues, globalHistories] = await Promise.all([
        DefaultService.getPlaysPlaysGet({ limit: 50, sort: 'newest' }).catch(() => [] as PlayOutput[]),
        leaguesRef.current
          ? Promise.resolve(leaguesRef.current)
          : DefaultService.getLeaguesLeaguesGet({}).catch(() => [] as LeagueOutput[]),
        // Every Global member's history (cached server-side): rating changes and positions
        getRatingHistories('global').catch(() => null),
      ]);
      leaguesRef.current = leagues;
      const plays = new Map<string, PlayOutput>((recent || []).map(p => [p.id, p]));
      await Promise.all(
        playEvents
          .filter(e => !plays.has(e.playId))
          .map(async e => {
            try {
              plays.set(e.playId, await DefaultService.getPlayPlaysPlayIdGet({ playId: e.playId }));
            } catch {
              // Deleted or unreadable play: the card falls back to the event payload.
            }
          })
      );
      const impacts = globalHistories
        ? computeRatingImpacts(playEvents.map(e => e.playId), Object.values(globalHistories))
        : {};
      const details: Record<string, FeedPlayDetails> = {};
      for (const e of playEvents) {
        const play = plays.get(e.playId);
        if (play) details[e.playId] = { ...buildPlayDetails(play, e.players, leagues), ratingImpacts: impacts[e.playId] };
      }
      return details;
    } catch {
      return {};
    }
  }, []);

  const fetchFeed = useCallback(async (token?: string, append: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await FeedService.getActivityFeedFeedGet({
        limit: initialLimit,
        nextToken: token || undefined,
      });

      // Use ref to access current events without adding to dependency array
      const newEvents = append ? [...eventsRef.current, ...response.events] : response.events;
      setEvents(newEvents);
      setNextToken(response.nextToken || null);
      setHasMore(!!response.nextToken);

      // Extract play IDs from NEW_PLAY events and batch fetch reactions
      const playIds = newEvents
        .filter((e: FeedEvent) => e.eventType === 'NEW_PLAY')
        .map((e: FeedEvent) => (e.payload as NewPlayPayload).playId);
      
      if (playIds.length > 0) {
        const newPlays = (append ? response.events : newEvents)
          .filter((e: FeedEvent) => e.eventType === 'NEW_PLAY')
          .map((e: FeedEvent) => e.payload as NewPlayPayload);
        fetchPlayDetails(newPlays).then(details => {
          setPlayDetailsMap(prev => (append ? { ...prev, ...details } : details));
        });
        const reactions = await fetchReactionsBatch(playIds);
        if (append) {
          setReactionsMap(prev => ({ ...prev, ...reactions }));
        } else {
          setReactionsMap(reactions);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load feed';
      setError(errorMessage);
      console.error('Error fetching feed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [initialLimit, fetchReactionsBatch, fetchPlayDetails]);

  const loadMore = useCallback(async () => {
    if (nextToken && !isLoading) {
      await fetchFeed(nextToken, true);
    }
  }, [nextToken, isLoading, fetchFeed]);

  const refresh = useCallback(async () => {
    setNextToken(null);
    await fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  return {
    events,
    isLoading,
    error,
    hasMore,
    reactionsMap,
    playDetailsMap,
    loadMore,
    refresh,
  };
}
